import React, { useContext, useState, useEffect } from "react";
import { Modal, Form, message } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { CartContext } from "./CartContext";
import api, { baseURL } from "./api";
import DeliveryAddress from "./DeliveryAddress";
import BillBreakdown from "./BillBreakdown";
import { Payment } from "./Payment";
import "./CartModal.css";

const CartModal = ({ closeModal }) => {
  const { cart, addToCart, removeFromCart, clearCart } = useContext(CartContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [userRole, setUserRole] = useState(localStorage.getItem("role") || "guest");
  const [priceType, setPriceType] = useState(location.state?.priceType || localStorage.getItem("priceType") || "sale");

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  const [customerName, setCustomerName] = useState("");
  const [customerContact, setCustomerContact] = useState("");
  const [paymentMode, setPaymentMode] = useState("");

  const [discountType, setDiscountType] = useState("percent");
  const [discountValue, setDiscountValue] = useState(0);

  const [isExistingUser, setIsExistingUser] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [vendorDetails, setVendorDetails] = useState(null);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState(null);
  const [isBreakdownModalVisible, setIsBreakdownModalVisible] = useState(false);
  const [taxConfig, setTaxConfig] = useState(null);

  const [form] = Form.useForm();

  const openBreakdownModal = () => setIsBreakdownModalVisible(true);
  const closeBreakdownModal = () => setIsBreakdownModalVisible(false);

  useEffect(() => {
    if (userRole === "guest") setPriceType("purchase");
  }, [userRole]);

  useEffect(() => {
    const fetchVendorDetails = async () => {
      try {
        const vendorId = localStorage.getItem("vendorId");
        const res = await api.get(`/getVendor/${vendorId}`);
        setVendorDetails(res.data);
      } catch (err) {
        console.error("Failed to fetch vendor details:", err);
      }
    };
    fetchVendorDetails();
  }, []);

  useEffect(() => {
    if (isExistingUser) {
      const fetchCustomers = async () => {
        try {
          const response = await fetch(`${baseURL}/getCustomerProductPricing`);
          const data = await response.json();
          if (Array.isArray(data)) setCustomers(data);
          else setCustomers([]);
        } catch (error) {
          console.error("Error fetching customers:", error);
          setCustomers([]);
        }
      };
      fetchCustomers();
    } else {
      setCustomers([]);
      setSelectedCustomer(null);
    }
  }, [isExistingUser]);

  const handleRoleChange = (newRole) => {
    setUserRole(newRole);
    setPriceType(newRole === "guest" ? "purchase" : "sale");
  };

  const handleQuantityChange = (item, operation) => {
    const updatedItem = { ...item };
    if (operation === "increment") updatedItem.quantity += 1;
    else if (operation === "decrement" && updatedItem.quantity > 1) updatedItem.quantity -= 1;
    addToCart(updatedItem);
  };

  const handleRemoveItem = (cartKey) => removeFromCart(cartKey);

  const fetchTaxConfig = async () => {
    try {
      const vendorId = localStorage.getItem("vendorId");
      const vendorRes = await api.get(`/getVendor/${vendorId}`);
      const vendor = vendorRes.data;

      const gstRes = await api.get(`/gst`);
      const gst = gstRes.data[0];
      if (!gst) return null;

      return vendor.state === gst.state
        ? { type: "CGST_SGST", cgst: gst.cgst, sgst: gst.sgst }
        : { type: "IGST", igst: gst.igst };
    } catch (error) {
      console.error("Failed to fetch tax config", error);
      return null;
    }
  };
  const calculateDiscountAmount = (subtotal) => {
  if (discountType === "percent") {
    return (subtotal * discountValue) / 100;
  } else if (discountType === "fixed") {
    return discountValue;
  }
  return 0;
};

 const calculateCartTotals = () => {
  let subtotalExclTax = 0; // ✅ before tax
  let subtotalInclTax = 0; // ✅ after tax (total)

  const updatedCart = cart.map((item) => {
    const addOnTotal = Array.isArray(item.addOns)
      ? item.addOns.reduce((sum, addOn) => sum + Number(addOn.price || 0), 0)
      : 0;

    const priceInclTax = Number(item.price || 0); // price coming from DB is INCLUSIVE
    const quantity = Number(item.quantity || 1);

    // Compute taxRate from config
    let taxRate = 0;
    if (taxConfig?.type === "CGST_SGST") {
      taxRate = (taxConfig.cgst + taxConfig.sgst) / 100;
    } else if (taxConfig?.type === "IGST") {
      taxRate = taxConfig.igst / 100;
    }

    // Convert inclusive price → exclusive
    const priceExclTax = priceInclTax / (1 + taxRate);

    const totalExcl = (priceExclTax + addOnTotal) * quantity;
    const totalIncl = (priceInclTax + addOnTotal) * quantity;

    subtotalExclTax += totalExcl;
    subtotalInclTax += totalIncl;

    return {
      ...item,
      totalPriceExcl: totalExcl,
      totalPriceIncl: totalIncl,
      vendorId: item.vendorId || localStorage.getItem("vendorId"),
      imgPath: item.imgPath || "default-image.jpg",
    };
  });

  // Apply discount only on subtotal (before tax)
  const discountAmount = calculateDiscountAmount(subtotalExclTax);
  const taxableAmount = subtotalExclTax - discountAmount;

  // Calculate taxes again on net taxable amount
  let cgst = 0,
    sgst = 0,
    igst = 0;

  if (taxConfig?.type === "CGST_SGST") {
    cgst = (taxableAmount * taxConfig.cgst) / 100;
    sgst = (taxableAmount * taxConfig.sgst) / 100;
  } else if (taxConfig?.type === "IGST") {
    igst = (taxableAmount * taxConfig.igst) / 100;
  }

  const total = taxableAmount + cgst + sgst + igst; // ✅ after tax

  return {
    subtotal: subtotalExclTax, // ✅ exc. tax
    discountAmount,
    cgst,
    sgst,
    igst,
    total, // ✅ inc. tax
    updatedCart,
  };
};

  const { subtotal, discountAmount, cgst, sgst, igst, total, updatedCart } = calculateCartTotals();

  const handlePlaceOrder = async () => {
    const token = localStorage.getItem("token");

  if (!token) {
    navigate("/signup");
    return;
  }
    
    setShowConfirmModal(true);
  };

  const handleCOD = async () => {
    const data = await api.get("getNextOrderId");
    localStorage.setItem("orderId", data.data.newOrderNumber);
    navigate("/SaleOrder");
    await handleConfirmOrder();
  };

  const handleConfirmOrder = async () => {
    if (priceType === "sale" && (!customerName || !customerContact || !paymentMode)) {
      alert("Please fill all customer details and payment mode.");
      return;
    }

    const vendorId = localStorage.getItem("vendorId");
    if (!vendorId) return console.error("Vendor ID missing");

    const taxCfg = await fetchTaxConfig();

    const subtotalExclTax = updatedCart.reduce((sum, item) => sum + item.totalPrice, 0);
    const discountAmt = calculateDiscountAmount(subtotalExclTax);
    const taxableAmount = subtotalExclTax - discountAmt;

    const taxesArray = [];
    if (taxCfg?.type === "CGST_SGST") {
      taxesArray.push({ gstType: "CGST", rate: taxCfg.cgst, amount: (taxableAmount * taxCfg.cgst) / 100 });
      taxesArray.push({ gstType: "SGST", rate: taxCfg.sgst, amount: (taxableAmount * taxCfg.sgst) / 100 });
    } else if (taxCfg?.type === "IGST") {
      taxesArray.push({ gstType: "IGST", rate: taxCfg.igst, amount: (taxableAmount * taxCfg.igst) / 100 });
    }

    const grandTotal = taxableAmount + taxesArray.reduce((sum, t) => sum + t.amount, 0);

    const orderId = localStorage.getItem("orderId");
    const orderDetails = {
  orderNumber: orderId,
  vendorId,
  cart: updatedCart.map(item => ({
    ...item,
    vendorId: item.vendorId || vendorId,
    imgPath: item.imgPath || "default-image.jpg",
  })),
  subtotal: subtotalExclTax,
  discountType,
  discountValue,
  discountAmount: discountAmt,
  taxes: taxesArray,
  total: grandTotal,
  priceType,
  date: new Date().toISOString(),
  customerName: priceType === "sale" ? customerName : "",
  customerContact: priceType === "sale" ? customerContact : "",
  paymentMode: priceType === "sale" ? paymentMode : "",
}; // <-- semicolon here


    try {
      const response = await fetch(`${baseURL}/placeOrder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderDetails),
      });
      const responseData = await response.json();

      if (response.ok) {
        clearCart();
        navigate("/SaleOrder", { state: { orderNumber: orderId, cart: updatedCart, subtotal: subtotalExclTax, taxes: taxesArray, total: grandTotal } });
        closeModal();
        setShowConfirmModal(false);
      } else console.error("Failed to place order:", responseData.error || responseData);
    } catch (error) {
      console.error("Error placing order:", error);
    }
  };

  const handleCancelOrder = () => setShowConfirmModal(false);

  const openDiscountModal = () => setShowDiscountModal(true);
  const closeDiscountModal = () => setShowDiscountModal(false);
  const handleApplyDiscount = () => closeDiscountModal();

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="close-btn" onClick={closeModal}>&#x2715;</button>
        <h2>Your Cart</h2>
        {cart.length === 0 ? (
          <p>Your cart is empty.</p>
        ) : (
          <>
            <div className="cart-items">
              {cart.map((item) => (
                <div className="cart-item" key={`${item.id}-${item.packetSize || "default"}`}>
                  <img src={`${baseURL}${item.imgPath}`} alt={item.name} className="item-image" onError={(e) => { e.target.src = "/path/to/default-image.jpg"; }} />
                  <div className="item-details">
                    <h3>{item.name}</h3>
                    {item.packetSize && <p>Packet Size: {item.packetSize}</p>}
                    <p>₹{item.price}</p>
                    {Array.isArray(item.addOns) && item.addOns.length > 0 && (
                      <div className="add-ons-list">
                        <p>Extras: {item.addOns.map((addOn) => `${addOn.name} (+₹${addOn.price})`).join(", ")}</p>
                      </div>
                    )}
                  </div>
                  <div className="item-quantity">
                    <button className="quantity-btn" onClick={() => handleQuantityChange(item, "decrement")}>-</button>
                    <span>{item.quantity}</span>
                    <button className="quantity-btn" onClick={() => handleQuantityChange(item, "increment")}>+</button>
                  </div>
                  <button className="remove-item-btn" onClick={() => handleRemoveItem(item.cartKey)}>Remove</button>
                </div>
              ))}
            </div>

            <div className="cart-summary">
              <h3>Total : ₹{subtotal.toFixed(2)}</h3>
              {discountAmount > 0 && <p>Discount : -₹{discountAmount.toFixed(2)}</p>}
              <button className="small-btn" onClick={openBreakdownModal}>View Breakdown</button>
              <BillBreakdown open={isBreakdownModalVisible} onClose={closeBreakdownModal} cart={cart} subtotal={subtotal} discountAmount={discountAmount} taxes={cgst + sgst + igst} total={total} />
              {priceType === "sale" && (
                <>
                  <div className="radio-group">
                    <label>
                      <input type="radio" checked={!isExistingUser} onChange={() => setIsExistingUser(false)} />
                      New User
                    </label>
                    <label>
                      <input type="radio" checked={isExistingUser} onChange={() => setIsExistingUser(true)} />
                      Existing User
                    </label>
                  </div>
                  {isExistingUser && (
                    <div className="form-group">
                      <label>Select Customer</label>
                      {customers.map((c) => (
  <option key={c._id} value={c._id}>
    {typeof c.customerName === "string" ? c.customerName : JSON.stringify(c.customerName)}
  </option>
))}

                    </div>
                  )}
                  <button className="small-btn" onClick={openDiscountModal}>Apply Discount</button>
                </>
              )}
            </div>

            <div className="place-order-btn-container">
              <button className="place-order-btn" onClick={handlePlaceOrder}>Place Order</button>
            </div>
          </>
        )}
      </div>

      {showConfirmModal && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <h3>Confirm Order</h3>
            {priceType === "sale" ? (
              <>
                <div className="form-group">
                  <label>Customer Name</label>
                  <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Enter customer name" />
                </div>
                <div className="form-group">
                  <label>Customer Contact No</label>
                  <input type="tel" value={customerContact} onChange={(e) => setCustomerContact(e.target.value)} placeholder="Enter contact number" />
                </div>
                <div className="form-group">
                  <label>Mode of Payment</label>
                  <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                    <option value="">Select payment mode</option>
                    <option value="online">Online</option>
                    <option value="UPI">UPI</option>
                  </select>
                </div>
                <p>Are you sure you want to place this order?</p>
                <div className="confirm-buttons">
                  <button className="confirm-btn" onClick={handleCOD}>Cash On Delivery</button>
                  <button className="cancel-btn" onClick={handleCancelOrder}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <p>Are you sure you want to place this order?</p>
                <DeliveryAddress vendorId={localStorage.getItem("vendorId")} onAddressSelect={setSelectedDeliveryAddress} />
                <div className="confirm-buttons">
                  {vendorDetails?.paymentModes?.online && <Payment handleConfirmOrder={handleConfirmOrder} vendorDetails={vendorDetails} selectedDeliveryAddress={selectedDeliveryAddress} totalAmount={total} />}
                  {vendorDetails?.paymentModes?.cod && <button className="confirm-btn" onClick={handleCOD}>Cash On Delivery</button>}
                  <button className="cancel-btn" onClick={handleCancelOrder}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showDiscountModal && (
        <div className="discount-modal-overlay">
          <div className="discount-modal">
            <h3>Apply Discount</h3>
            <div className="form-group">
              <label>Discount Type</label>
              <select value={discountType} onChange={(e) => setDiscountType(e.target.value)}>
                <option value="percent">Percent</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
            <div className="form-group">
              <label>Discount Value</label>
              <input type="number" value={discountValue} onChange={(e) => setDiscountValue(Number(e.target.value))} />
            </div>
            <button className="confirm-btn" onClick={handleApplyDiscount}>Apply</button>
            <button className="cancel-btn" onClick={closeDiscountModal}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartModal;
